import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Truck, Search, MoreVertical, ChevronRight, History as HistoryIcon, Ban, RotateCcw, Archive, PauseCircle, AlertOctagon } from 'lucide-react';
import { StatusBadge, CONTROL_META } from './fleetUtils';
import TimelineDialog from './TimelineDialog';

const ACTION_META = {
  immobiliser: { label: 'Immobiliser', endpoint: (id) => `${API}/fleet/vehicles/${id}/immobilize`, color: 'text-red-600' },
  inactif: { label: 'Rendre inactif', endpoint: (id) => `${API}/fleet/vehicles/${id}/set-inactive`, color: 'text-gray-600' },
  remettre: { label: 'Remettre en service', endpoint: (id) => `${API}/fleet/vehicles/${id}/return-to-service`, color: 'text-green-700' },
  reactiver: { label: 'Réactiver', endpoint: (id) => `${API}/fleet/vehicles/${id}/return-to-service`, color: 'text-green-700' },
  archiver: { label: 'Archiver', endpoint: (id) => `${API}/fleet/vehicles/${id}/archive`, color: 'text-gray-700' },
};

export const StatusActionDialog = ({ vehicle, action, open, onClose, onDone }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ reason: '', comment: '', linked_ref: '', date: today, immediate: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ reason: '', comment: '', linked_ref: '', date: today, immediate: true }); }, [open]);
  if (!open || !vehicle || !action) return null;
  const meta = ACTION_META[action];
  const submit = async () => {
    if (!f.reason.trim() && action !== 'remettre' && action !== 'reactiver') { alert('Le motif est obligatoire'); return; }
    setBusy(true);
    try {
      const parts = [f.reason.trim(), f.linked_ref && `Lié à : ${f.linked_ref}`, f.comment && `Commentaire : ${f.comment}`,
        f.date !== today && `Date : ${f.date}`, action === 'immobiliser' && !f.immediate && '(immobilisation différée)'].filter(Boolean);
      const res = await axios.post(meta.endpoint(vehicle.vehicle_id), { reason: parts.join(' — ') || null }, { withCredentials: true });
      if (res.data.active_mission_warning) alert('⚠️ Véhicule en mission : la mission en cours est conservée, toute nouvelle affectation est bloquée.');
      onDone?.(); onClose();
    } catch (e) { alert(e.response?.data?.detail || 'Action refusée'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="status-action-dialog">
        <DialogHeader><DialogTitle>{meta.label} — {vehicle.plate_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {(action === 'remettre' || action === 'reactiver') ? (
            <p className="text-sm text-gray-600">Confirmation explicite : le véhicule redeviendra sélectionnable par les salariés. Refusé si une anomalie bloquante reste ouverte.</p>
          ) : (
            <div><Label>Motif *</Label>
              <Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="status-action-reason" /></div>
          )}
          <div><Label>Panne ou accident associé (référence, optionnel)</Label>
            <Input value={f.linked_ref} onChange={(e) => setF({ ...f, linked_ref: e.target.value })} placeholder="ex : iss_xxx, acc_xxx" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
            {action === 'immobiliser' && (
              <label className="flex items-end gap-2 text-sm pb-2">
                <input type="checkbox" checked={f.immediate} onChange={(e) => setF({ ...f, immediate: e.target.checked })} /> Immédiat
              </label>
            )}
          </div>
          <div><Label>Commentaire</Label>
            <Textarea rows={2} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} /></div>
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="status-action-submit">{meta.label}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RequestImmoDialog = ({ vehicle, open, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open || !vehicle) return null;
  const submit = async () => {
    if (!reason.trim()) { alert('Le motif est obligatoire'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/fleet/issues`, {
        vehicle_id: vehicle.vehicle_id, item_label: "Demande d'immobilisation",
        description: reason.trim(), severity: 'bloquant', immobilization_recommended: true,
      }, { withCredentials: true });
      alert('Demande envoyée : le chef de parc et le Super Admin ont reçu une alerte critique.');
      onDone?.(); onClose(); setReason('');
    } catch (e) { alert(e.response?.data?.detail || 'Demande refusée'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="request-immo-dialog">
        <DialogHeader><DialogTitle>Demander une immobilisation — {vehicle.plate_number}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">Seuls le chef de parc et le Super Admin peuvent immobiliser. Votre demande crée une alerte critique.</p>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif de la demande (obligatoire)" data-testid="request-immo-reason" />
        <Button className="w-full" onClick={submit} disabled={busy} data-testid="request-immo-submit">Envoyer la demande</Button>
      </DialogContent>
    </Dialog>
  );
};

const SiteTransferDialog = ({ vehicle, sites, open, onClose, onDone }) => {
  const [newSite, setNewSite] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNewSite(''); setReason(''); setError(null); } }, [open]);
  if (!vehicle) return null;
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/fleet/usage/vehicles/${vehicle.vehicle_id}/transfer-site`,
        { new_site_id: newSite, reason }, { withCredentials: true });
      onDone?.(); onClose();
    } catch (e) { setError(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} data-testid="site-transfer-dialog">
        <DialogHeader><DialogTitle>Transférer de site — {vehicle.plate_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Site actuel : {sites.find(s => s.site_id === vehicle.site_id)?.name || 'Flotte commune (aucun site)'}</p>
          <div>
            <Label>Nouveau site *</Label>
            <select value={newSite} onChange={(e) => setNewSite(e.target.value)}
              className="w-full px-2 py-2 border rounded-md bg-white text-sm" data-testid="site-transfer-select">
              <option value="">— Choisir un site —</option>
              {sites.filter(s => s.site_id !== vehicle.site_id).map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Motif *</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="site-transfer-reason" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="site-transfer-error">{error}</p>}
          <Button className="w-full" disabled={busy || !newSite || !reason.trim()} onClick={submit} data-testid="site-transfer-submit">
            Transférer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ParcTab = ({ vehicles, sites, employees, fleetMe, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timelineVehicle, setTimelineVehicle] = useState(null);
  const [actionCtx, setActionCtx] = useState(null);
  const [requestImmoV, setRequestImmoV] = useState(null);
  const [transferV, setTransferV] = useState(null);
  const [deadlineMap, setDeadlineMap] = useState({});
  const isFM = fleetMe?.is_fleet_manager;

  useEffect(() => {
    axios.get(`${API}/fleet/deadlines`, { withCredentials: true }).then(r => {
      const m = {};
      const order = { expire: 0, urgent: 1, a_prevoir: 2, a_jour: 3 };
      r.data.forEach(d => {
        const cur = m[d.vehicle_id];
        if (!cur || order[d.status] < order[cur.status]) m[d.vehicle_id] = d;
      });
      setDeadlineMap(m);
    }).catch(() => {});
  }, [vehicles]);

  const handleDelete = async (vehicle) => {
    if (!window.confirm(`Supprimer ${vehicle.plate_number} ? (archivage automatique si historique existant)`)) return;
    try {
      const res = await axios.delete(`${API}/vehicles/${vehicle.vehicle_id}`, { withCredentials: true });
      alert(res.data.message); onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'Suppression refusée'); }
  };

  const handleAssign = async (vehicleId, userId) => {
    try {
      await axios.put(`${API}/vehicles/${vehicleId}/assign`, { user_id: userId || null }, { withCredentials: true });
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || "Échec de l'assignation"); }
  };

  const filtered = vehicles.filter((v) => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || (v.plate_number || '').toLowerCase().includes(s) || (v.brand || '').toLowerCase().includes(s) || (v.model || '').toLowerCase().includes(s);
    const matchSite = siteFilter === 'all' || (siteFilter === 'none' ? !v.site_id : v.site_id === siteFilter);
    const matchStatus = statusFilter === 'all' || v.operational_status === statusFilter;
    return matchSearch && matchSite && matchStatus;
  });

  const dlLabel = (d) => {
    if (!d) return '—';
    const state = d.status === 'expire' ? '🔴 expirée' : d.status === 'urgent' ? '🟠' : d.status === 'a_prevoir' ? '🟡' : '🟢';
    return `${d.label} ${d.due_date ? `(${d.due_date})` : d.due_km ? `(${d.due_km} km)` : ''} ${state}`;
  };

  return (
    <div className="space-y-4">
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <Search className="w-5 h-5 text-blue-600" />
          <Input placeholder="Rechercher (plaque, marque, modèle)..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="flex-1 min-w-[180px]" data-testid="parc-search" />
          <select className="px-3 py-2 border rounded-md bg-white text-sm" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} data-testid="parc-site-filter">
            <option value="all">Tous les sites</option>
            <option value="none">Flotte commune</option>
            {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
          </select>
          <select className="px-3 py-2 border rounded-md bg-white text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="parc-status-filter">
            <option value="all">Tous statuts</option>
            <option value="actif_conforme">Conforme</option>
            <option value="actif_a_surveiller">À surveiller</option>
            <option value="immobilise">Immobilisé</option>
            <option value="en_reparation">En réparation</option>
            <option value="inactif">Inactif</option>
            <option value="archive">Archivé</option>
          </select>
          <span className="text-blue-700 text-sm">{filtered.length} véhicule(s)</span>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <Card className="col-span-full"><CardContent className="p-8 text-center">
            <Truck className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Aucun véhicule trouvé avec ces filtres</p>
          </CardContent></Card>
        ) : filtered.map((v) => {
          const dimmed = ['immobilise', 'en_reparation', 'inactif', 'archive'].includes(v.operational_status);
          const dl = deadlineMap[v.vehicle_id];
          const canImmo = !['immobilise', 'en_reparation', 'archive'].includes(v.operational_status);
          const canReturn = ['immobilise', 'en_reparation'].includes(v.operational_status);
          const canReactivate = v.operational_status === 'inactif';
          return (
            <Card key={v.vehicle_id} className={`border-2 transition-all ${dimmed ? 'opacity-75 bg-gray-50' : 'hover:border-blue-300 hover:shadow-lg'}`}
              data-testid={`parc-vehicle-${v.vehicle_id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-lg font-bold">{v.plate_number}</p>
                    <p className="text-xs text-gray-500">{v.brand} {v.model} · {v.site_id ? (sites.find(s => s.site_id === v.site_id)?.name || v.site_id) : 'Flotte commune'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <StatusBadge status={v.operational_status} />
                    {isFM && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`vehicle-actions-${v.vehicle_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canImmo && <DropdownMenuItem className="text-red-600" onClick={() => setActionCtx({ v, a: 'immobiliser' })} data-testid={`action-immobiliser-${v.vehicle_id}`}><Ban className="w-4 h-4 mr-2" />Immobiliser</DropdownMenuItem>}
                          {canReturn && <DropdownMenuItem className="text-green-700" onClick={() => setActionCtx({ v, a: 'remettre' })} data-testid={`action-remettre-${v.vehicle_id}`}><RotateCcw className="w-4 h-4 mr-2" />Remettre en service</DropdownMenuItem>}
                          {canReactivate && <DropdownMenuItem className="text-green-700" onClick={() => setActionCtx({ v, a: 'reactiver' })} data-testid={`action-reactiver-${v.vehicle_id}`}><RotateCcw className="w-4 h-4 mr-2" />Réactiver</DropdownMenuItem>}
                          {canImmo && <DropdownMenuItem onClick={() => setActionCtx({ v, a: 'inactif' })} data-testid={`action-inactif-${v.vehicle_id}`}><PauseCircle className="w-4 h-4 mr-2" />Rendre inactif</DropdownMenuItem>}
                          {isFM && <DropdownMenuItem onClick={() => setTransferV(v)} data-testid={`action-transfer-${v.vehicle_id}`}><ChevronRight className="w-4 h-4 mr-2" />Transférer de site</DropdownMenuItem>}
                          {v.operational_status !== 'archive' && <DropdownMenuItem onClick={() => setActionCtx({ v, a: 'archiver' })} data-testid={`action-archiver-${v.vehicle_id}`}><Archive className="w-4 h-4 mr-2" />Archiver</DropdownMenuItem>}
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(v)} data-testid={`delete-btn-${v.vehicle_id}`}>Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <div className="text-xs space-y-1 text-gray-600">
                  {v.active_assignment ? (
                    <p className="text-orange-700 font-medium" data-testid={`vehicle-in-use-${v.vehicle_id}`}>🚚 Utilisé par {v.active_assignment.user_name}</p>
                  ) : v.assigned_user_id ? (
                    <p className="text-green-700">Assigné à {employees.find(e => e.user_id === v.assigned_user_id)?.name || 'Salarié'}</p>
                  ) : <p className="text-gray-400">Aucun chauffeur en cours</p>}
                  <p>Anomalies ouvertes : <strong>{v.open_issues_count}</strong>
                    {v.blocking_issues_count > 0 && <span className="text-red-600 font-bold"> dont {v.blocking_issues_count} bloquante(s)</span>}
                    {v.to_fix_issues_count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-600 text-white text-xs font-semibold" data-testid={`vehicle-to-fix-${v.vehicle_id}`}>À corriger avant départ ({v.to_fix_issues_count})</span>}</p>
                  <p>Prochaine échéance : {dlLabel(dl)}</p>
                  <p>Dernier contrôle : <span className={`px-1.5 py-0.5 rounded ${CONTROL_META[v.control_status]?.cls}`}>{CONTROL_META[v.control_status]?.label}</span></p>
                </div>
                <div className="flex flex-wrap gap-1.5 border-t pt-2 items-center">
                  <Link to={`/admin/b2b/vehicles/${v.vehicle_id}/details`}>
                    <Button variant="outline" size="sm" data-testid={`details-btn-${v.vehicle_id}`}><ChevronRight className="w-4 h-4" /> Fiche</Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={() => setTimelineVehicle(v)} data-testid={`timeline-btn-${v.vehicle_id}`}>
                    <HistoryIcon className="w-4 h-4" />
                  </Button>
                  {isFM ? (
                    canImmo ? (
                      <Button variant="outline" size="sm" className="text-red-600 ml-auto" onClick={() => setActionCtx({ v, a: 'immobiliser' })} data-testid={`immobilize-btn-${v.vehicle_id}`}>
                        <Ban className="w-4 h-4 mr-1" /> Immobiliser
                      </Button>
                    ) : canReturn ? (
                      <Button variant="outline" size="sm" className="text-green-700 ml-auto" disabled={v.blocking_issues_count > 0}
                        title={v.blocking_issues_count > 0 ? 'Anomalies bloquantes encore ouvertes' : ''}
                        onClick={() => setActionCtx({ v, a: 'remettre' })} data-testid={`return-btn-${v.vehicle_id}`}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Remettre en service
                      </Button>
                    ) : null
                  ) : canImmo && (
                    <Button variant="outline" size="sm" className="text-orange-700 ml-auto" onClick={() => setRequestImmoV(v)} data-testid={`request-immo-btn-${v.vehicle_id}`}>
                      <AlertOctagon className="w-4 h-4 mr-1" /> Demander une immobilisation
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <TimelineDialog vehicle={timelineVehicle} open={!!timelineVehicle} onClose={() => setTimelineVehicle(null)} />
      <StatusActionDialog vehicle={actionCtx?.v} action={actionCtx?.a} open={!!actionCtx} onClose={() => setActionCtx(null)} onDone={onRefresh} />
      <RequestImmoDialog vehicle={requestImmoV} open={!!requestImmoV} onClose={() => setRequestImmoV(null)} onDone={onRefresh} />
      <SiteTransferDialog vehicle={transferV} sites={sites} open={!!transferV} onClose={() => setTransferV(null)} onDone={onRefresh} />
    </div>
  );
};

export default ParcTab;
